import { memo, useMemo, useRef } from "react";
import classNames from "classnames";

import answerStyles from "../messageContainer/answerContainer.module.scss";
import styles from "./style.module.scss";

import { AssistanceIcon } from "../userName";
import { ScrollNavigation } from "./ScrollNavigation";

import { Image } from "./image";

import { IChatModel, IImage, IMessage } from "redux/actions";
import { useSelector } from "redux/hooks";

import { useWindowSize } from "hooks/useWindowSize";
import { ErrorMessageDisplay } from "../ErrorMessageDisplay/ErrorMessageDisplay";

interface IProps {
  message: IMessage;
  IGLoading: boolean;
  share?: boolean;
  messageIndex?: number;
  onRegenerate?: (
    messageIndex: number,
    chatModal: IChatModel,
    regenerateModel?: boolean
  ) => void;
}

export interface IImageToMap {
  id: number;
  size: string;
  upscaled_images_ids: number[];
  images: IImage[];
}

export const ImageCarousel = memo(
  ({ message, IGLoading, messageIndex, onRegenerate }: IProps) => {
    const { id, images } = message;
    const { theme } = useSelector((state) => state.authReducer);
    const { width } = useWindowSize();
    const carouselRef = useRef<HTMLDivElement>(null);
    const maxImageDimension = width <= 576 ? 140 : 400; //220
    const edgeDistance = width <= 576 ? 36 : 50;

    const allImages: IImageToMap[] = useMemo(() => {
      return images?.reduce((acc: IImageToMap[], image: IImage) => {
        const existingImage = acc.find((val) =>
          val.upscaled_images_ids.includes(image.id)
        );

        if (existingImage) {
          existingImage.images.push(image);
        } else {
          const imageObject: IImageToMap = {
            id: image.id,
            size: image.size,
            upscaled_images_ids:
              image.upscaled_images && image.upscaled_images.length
                ? image.upscaled_images.map(
                    (upscaledImage) => upscaledImage.upscale_image_id
                  )
                : [],
            images: [image],
          };
          acc.push(imageObject);
        }

        return acc;
      }, []);
    }, [images]);

    return (
      <div className={answerStyles.container}>
        <div className={answerStyles.leftContainer}>
          <div>
            <AssistanceIcon
              showBg={true}
              className={classNames({
                [answerStyles.assistanceIconAnimation]:
                  allImages?.length === 0 &&
                  !message?.content &&
                  IGLoading === true,
              })}
            />
          </div>
          <div className="overflow-hidden">
            <ScrollNavigation
              scrollContainerRef={carouselRef}
              scrollDistance={maxImageDimension}
              edgeDistance={edgeDistance}
              className="flex overflow-x-auto whitespace-nowrap items-center gap-2 pt-2"
            >
              {allImages.map((image, index) => (
                <Image
                  key={`image ${index}`}
                  messageId={Number(id)}
                  chatId={message.chat_id as number}
                  images={image.images}
                  initialIndex={image.images.length - 1}
                />
              ))}
            </ScrollNavigation>

            {message.content && (
              <div
                className={classNames(styles.text, {
                  [styles.light]: theme === "light",
                  [styles.dark]: theme === "dark",
                })}
              >
                {message.content}
              </div>
            )}

            {message.err_message && (
              <ErrorMessageDisplay
                message={message}
                onRegenerate={onRegenerate}
                messageIndex={messageIndex}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);
